import { createSelector } from "reselect";
import AppState from "../AppState";
import { getCategories } from "../category/CategorySelectors";
import { ColorProduct, Product } from "../product/ProductTypes";
import { selectProductFetched } from "../product/ProductSelectors";
import { calculateV1Progress } from "../../utils/ProductUtil";

export const selectFindProductsState = (state: AppState) => {
  return state.findProducts;
};

export const selectTopCategoriesFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.topCategory;
  },
);

export const selectChildCategoriesFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.childCategory;
  },
);

export const selectCategoriesFromFindProducts = createSelector(
  [selectTopCategoriesFromFindProducts, selectChildCategoriesFromFindProducts],
  (catArray, childCategory) => {
    return getCategories(catArray, childCategory, null, []);
  },
);

export const selectProductsFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.products || [];
  },
);

export const selectProductsSizeFromFindProducts = createSelector(
  [selectProductsFromFindProducts],
  (products) => {
    return products.length;
  },
);

export const selectIsSubmitting = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.isSubmitting;
  },
);

export const selectIsSubmitted = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.isSubmitted;
  },
);

export const selectTopCategoriesForFindProductsModalBasedOnCatalog = (
  state: AppState,
) => {
  return state.category.findProductsModalTopCategory;
};

export const selectChildCategoriesBasedOnCategorySelectionForFindProducts = (
  state: AppState,
) => {
  return state.category.findProductsModalChildCategory;
};

export const selectCategoriesForFindProducts = createSelector(
  [
    selectTopCategoriesForFindProductsModalBasedOnCatalog,
    selectChildCategoriesBasedOnCategorySelectionForFindProducts,
  ],
  (catArray, childCategory) => {
    return getCategories(catArray, childCategory, null, []);
  },
);

export const selectGetProductListForFindProductsModal = (state: AppState) => {
  return state.findProducts.productList;
};

export const selectAreAllPagesFetched = (state: AppState) => {
  return state.findProducts && state.findProducts.allPagesFetched;
};

export const selectGetProductIds = (state: AppState) => {
  return state.findProducts && state.findProducts.productIds;
};

export const selectAddingRemovingProductIdsList = (state: AppState) => {
  return state.findProducts && state.findProducts.addingRemovingProductIdsList;
};

export const selectProductByIdForFindProducts = (state: AppState) => {
  return state.findProducts && state.findProducts.productById;
};

export const selectProductsColorDataForFindProducts = (state: AppState) => {
  return state.findProducts?.productsColorData ?? {};
};

export const selectIsFetchingProductIds = (state: AppState) => {
  return state.findProducts && state.findProducts.isFetchingProductIds;
};

export const selectIsProductIdsFetched = (state: AppState) => {
  return state.findProducts && state.findProducts.isProductIdsFetched;
};

export const selectIsFetchingFindProductDetails = (state: AppState): boolean => {
  return Boolean(state.findProducts?.isFetchingProductDetails);
};

/** True while find-products modal is loading details or color swatch data . */
export const selectIsFindProductsAssetsLoading = (state: AppState): boolean => {
  const fp = state.findProducts;
  if (!fp) return false;
  if (fp.findProductsPageLoadPending) return true;
  if (fp.isFetchingProductDetails) return true;
  return (fp.findProductsColorsInflightCount ?? 0) > 0;
};

/**
 * Merge find-products `productById` with `productsColorData` (modal color fetch).
 */
export const selectFindProductsMergedProductRow = (
  state: AppState,
  productId: string,
): Product | undefined => {
  const product = state.findProducts?.productById?.[productId];
  const productColorData = state.findProducts?.productsColorData?.[
    productId
  ] as ColorProduct | undefined;
  if (!product && !productColorData) return undefined;
  const row = (product ?? {}) as Product;
  const colors =
    row.colors && row.colors.length > 0
      ? row.colors
      : (productColorData?.colors ?? []);
  const mergedProduct = {
    ...row,
    productId: row.productId || productId,
    colors,
    hasColors: row.hasColors ?? Boolean(colors.length),
    hasManageableColors:
      row.hasManageableColors ??
      Boolean(productColorData?.hasManageableColors ?? false),
    hasSequenceableColors:
      row.hasSequenceableColors ??
      Boolean(productColorData?.hasSequenceableColors ?? false),
    colorVariationAttributeId:
      row.colorVariationAttributeId ||
      productColorData?.colorVariationAttributeId ||
      "",
    categorySpecific:
      row.categorySpecific ?? productColorData?.categorySpecific ?? [],
  } as Product;
  return mergedProduct.productId ? mergedProduct : undefined;
};

/**
 * Row for Find Products UI + modals opened from it: find-products data wins over main
 * `products` when both exist so search/category do not depend on the grid cache.
 * Falls back to `selectProductFetched` when find has no row (e.g. grid-only open).
 */
export const selectDisplayProductForFindProductsModal = (
  state: AppState,
  productId: string,
): Product | undefined => {
  const findProduct = selectFindProductsMergedProductRow(state, productId);
  const gridProduct = selectProductFetched(state, productId);
  if (!findProduct && !gridProduct) return undefined;
  if (!gridProduct) return findProduct;
  if (!findProduct) return gridProduct;
  const colors =
    findProduct.colors && findProduct.colors.length > 0
      ? findProduct.colors
      : (gridProduct.colors ?? []);
  return {
    ...gridProduct,
    ...findProduct,
    colors,
  };
};

/**
 * Determinate 0–100 loading % for the current Find Products page.
 * product IDs and counts productDetailsLoaded + productColorDetailsLoaded.
 * Cache key is the page number string so each page is monotonic independently.
 */
export const selectFindProductsLoadingPercentage = (
  state: AppState,
  page: number = 1,
  pageSize: number = 24,
): number => {
  const fp = state.findProducts;
  const productIds = fp.productIds ?? [];
  const N = productIds.length;
  if (N === 0) return 0;

  const productById = fp.productById ?? {};
  const productsColorData = fp.productsColorData ?? {};
  const progressCache = fp.progressCache ?? {};

  const startIndex = (page - 1) * pageSize;
  const endIndex = page * pageSize;
  const pageProductIds = productIds.slice(startIndex, endIndex);

  if (pageProductIds.length === 0) return 100;

  const cacheKey = `${page}`;

  let totalItemsToLoad = 0;
  let loadedItems = 0;

  for (const id of pageProductIds) {
    const p = productById[id];
    totalItemsToLoad++;
    if (p?.productDetailsLoaded) {
      loadedItems++;
      if (p.hasColors) {
        totalItemsToLoad++;
        if (productsColorData[id]?.productColorDetailsLoaded) {
          loadedItems++;
        }
      }
    }
  }

  return calculateV1Progress(totalItemsToLoad, loadedItems, cacheKey, progressCache);
};
